import { useState } from 'react';

import { Box, Layers } from 'lucide-react';

import { useMyPermissions } from '@/api/getMyPermissions';
import { useGetSandboxes } from '@/api/getSandboxes';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { Permissions, WILDCARD, hasPermission } from '@/models/permission';

/**
 * The scope a Grant may be created on, capped by the viewer's own
 * `admin:manage_permissions` coverage (#419 AC 6): only Sandboxes they administer
 * are offered, and All Sandboxes only when they hold it themselves.
 *
 * A matcher rather than `usePermissionCheck`, which cannot be called once per
 * candidate Sandbox.
 */
export const useGrantScope = (gameCode: string) => {
  const { data: myPermissions } = useMyPermissions();
  const { data: sandboxData } = useGetSandboxes({ game_code: gameCode });
  const [picked, setPicked] = useState('');

  const canGrantOn = (sandboxCode: string) =>
    hasPermission(
      myPermissions?.roles ?? [],
      Permissions.admin.managePermissions,
      gameCode,
      sandboxCode,
    );

  const canGrantAllSandboxes = canGrantOn(WILDCARD);
  const sandboxes = (sandboxData?.sandboxes ?? []).filter(({ code }) =>
    canGrantOn(code),
  );

  // Until something is picked, the widest scope the viewer may grant on — which is
  // also the safest default to *show*: it is the one they hold.
  const scope =
    picked || (canGrantAllSandboxes ? WILDCARD : (sandboxes[0]?.code ?? ''));

  return { scope, setScope: setPicked, sandboxes, canGrantAllSandboxes };
};

type GrantScope = ReturnType<typeof useGrantScope>;

/** All Sandboxes / one Sandbox, the scope half of every Grant the SPA creates. */
export const ScopePicker = ({
  label,
  scope,
  setScope,
  sandboxes,
  canGrantAllSandboxes,
}: GrantScope & { label: string }) => {
  const allSandboxes = scope === WILDCARD;

  return (
    <div className="grid gap-2">
      <Label>{label}</Label>
      <RadioGroup
        value={allSandboxes ? 'all' : 'one'}
        onValueChange={(value) => {
          setScope(value === 'all' ? WILDCARD : (sandboxes[0]?.code ?? ''));
        }}
        className="gap-1.5"
      >
        <Label
          className={cn(
            'flex items-center gap-3 rounded-md border p-3 text-sm font-normal',
            !canGrantAllSandboxes && 'cursor-not-allowed opacity-50',
          )}
        >
          <RadioGroupItem value="all" disabled={!canGrantAllSandboxes} />
          <Layers size={16} className="text-primary" />
          <span className="font-medium">All Sandboxes</span>
          <span className="flex-1 text-xs text-muted-foreground">
            {canGrantAllSandboxes
              ? 'The Role applies to every Sandbox of the Game, present and future.'
              : 'Not available: your own permission-management Grant covers a Sandbox only.'}
          </span>
        </Label>
        <Label className="flex items-center gap-3 rounded-md border p-3 text-sm font-normal">
          <RadioGroupItem value="one" />
          <Box size={16} className="text-muted-foreground" />
          <span className="font-medium">One Sandbox</span>
          <span className="flex-1" />
          {!allSandboxes && (
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger size="sm" className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sandboxes.map((sandbox) => (
                  <SelectItem key={sandbox.code} value={sandbox.code}>
                    {sandbox.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Label>
      </RadioGroup>
    </div>
  );
};
